import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, CheckCircle2, ExternalLink, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export function ReplyNotifications() {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tracking/replies/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, [token]);

  const fetchReplies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-tracking/replies?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplies(res.data.replies || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (replyId) => {
    try {
      await axios.put(`${API}/email-tracking/replies/${replyId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplies(prev => prev.map(r => r.id === replyId ? { ...r, is_read: true } : r));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  // Poll unread count every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleOpen = () => {
    if (!open) fetchReplies();
    setOpen(!open);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
        data-testid="reply-notifications-btn"
      >
        <MessageSquare className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-0.5 text-[10px] font-bold text-white bg-amber-500 rounded-full" data-testid="reply-unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="reply-notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800">Email Replies</h3>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-400">Loading...</div>
            ) : replies.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">No email replies yet</div>
            ) : (
              replies.map(reply => (
                <div
                  key={reply.id}
                  className={`px-4 py-3 hover:bg-slate-50 transition-colors ${!reply.is_read ? 'bg-amber-50/50 border-l-2 border-l-amber-400' : ''}`}
                  data-testid={`reply-notification-${reply.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${!reply.is_read ? 'bg-amber-500' : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {reply.sender_name || reply.sender_email}
                        </p>
                        <span className="text-[10px] text-slate-400 whitespace-nowrap">{timeAgo(reply.received_at || reply.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-600 truncate">{reply.subject || '(No subject)'}</p>
                      <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                        {(reply.stripped_text || reply.body_plain || '').slice(0, 120)}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {reply.entity_type && reply.entity_id && (
                          <a
                            href={`/${reply.entity_type}s`}
                            className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => setOpen(false)}
                          >
                            <ExternalLink className="h-3 w-3" />
                            View {reply.entity_type}
                          </a>
                        )}
                        {!reply.is_read && (
                          <button
                            onClick={() => markRead(reply.id)}
                            className="inline-flex items-center gap-1 text-[10px] text-amber-700 hover:text-amber-900 font-medium"
                            data-testid={`mark-read-notif-${reply.id}`}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {replies.length > 0 && (
            <div className="px-4 py-2 border-t bg-slate-50 text-center">
              <a href="/email-analytics" className="text-xs text-blue-600 hover:text-blue-800 font-medium" onClick={() => setOpen(false)}>
                View all in Email Analytics
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
